import {
  Box,
  Button,
  Container,
  Divider,
  Flex,
  Grid,
  GridItem,
  Heading,
  Image,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { BsInstagram } from "react-icons/bs";
import { FaLinkedinIn } from "react-icons/fa";
import { Link, useLoaderData } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import { Company } from "../../@types";
import {
  arrowBack,
  arrowSliderLeft,
  arrowSliderRight,
  iconFacebook,
  iconGlobe,
} from "../../assets/images";
import {
  AC,
  AL,
  AM,
  AP,
  BA,
  CE,
  DF,
  ES,
  GO,
  MA,
  MG,
  MS,
  MT,
  PA,
  PB,
  PE,
  PI,
  PR,
  RJ,
  RN,
  RO,
  RR,
  RS,
  SC,
  SE,
  SP,
  TO,
} from "../../assets/images/states";
import { Bar, ChartExperience } from "../../components/Charts";
import CTAWithBackgroundImage from "../../components/CTA";
import { SocialButton } from "../../components/Footer";
import ImageSelo from "../../components/Selo/ImageSelo";
import { isValidUrl } from "../../utils/validators";

const settings = {
  dots: false,
  arrows: false,
  fade: false,
  infinite: true,
  slidesToShow: 1,
  slidesToScroll: 1,
  speed: 800,
  swipeToSlide: true,
};

const UF_TO_FLAG: Record<string, string> = {
  AC,
  AL,
  AM,
  AP,
  BA,
  CE,
  DF,
  ES,
  GO,
  MA,
  MG,
  MS,
  MT,
  PA,
  PB,
  PE,
  PI,
  PR,
  RJ,
  RN,
  RO,
  RR,
  RS,
  SC,
  SE,
  SP,
  TO,
};

const formatCertificationDate = (timestamp: string): string => {
  const date = new Date(+timestamp);
  const month = date.toLocaleString("pt-br", { month: "long" });
  return `Atualizado em ${month} de ${date.getFullYear()}`;
};

const getCompanySize = (employees: number): string => {
  if (employees < 100) return "Pequena";
  if (employees > 1000) return "Grande";
  return "Média";
};

const cleanSectorString = (sector: string | null | undefined): string => {
  if (!sector) return "";
  return sector.replace(/\d+/g, "").replaceAll(".", "");
};

export function Detalhe() {
  const company = useLoaderData() as Company;
  const [sliderCarrousel, setSlider] = useState<Slider | null>();

  const {
    formattedCertificationDate,
    companySize,
    cleanedSector,
    companyImages,
    flagImage,
  } = useMemo(
    () => ({
      formattedCertificationDate: formatCertificationDate(company.DataCertificacao),
      companySize: getCompanySize(company.TotalEmployees),
      cleanedSector: cleanSectorString(company.sector),
      companyImages: company.photos ? company.photos.split(",") : null,
      flagImage: UF_TO_FLAG[company.UF] || null,
    }),
    [company]
  );

  return (
    <>
      <Container maxW={["100%", "1170px"]} overflow="hidden">
        <Stack p={["60px 0px", "30px 0 120px"]} align="start" spacing={["40px", "36px"]}>
          <Button
            as="a"
            href="/"
            variant="link"
            gap="16px"
            textTransform="uppercase"
            fontSize="12px"
            letterSpacing="-0.0047em"
            color="#11131C"
            pb={["20px", "unset"]}
          >
            <Image
              src={arrowBack}
              alt="Arrow back"
              h="8px"
              display={["none", "initial"]}
            />
            voltar para empresas
          </Button>
          <Grid
            templateColumns={["repeat(1, 1fr)", "repeat(5, 1fr)"]}
            height={["auto", "180px"]}
            gap={["40px", "12px"]}
            w="100%"
            pt={["0", "20px"]}
          >
            <GridItem colSpan={3} pl={["0px", "30px"]}>
              <Flex
                height="310px"
                gap={["20px", "22px"]}
                color="black"
                justifyContent="flex-start"
                alignItems={"center"}
              >
                <Box w={["100%", "165px"]} height={["233px", "233px"]} overflow="hidden">
                  <ImageSelo company={company} />
                </Box>
                <Box>
                  <Stack spacing={0}>
                    <Heading
                      width="270px"
                      letterSpacing={["-1.14px", "-0.0112em"]}
                      fontSize={[
                        company.fantasyName.length > 30 ? "26px" : "30px",
                        company.fantasyName.length > 30 ? "36px" : "34px",
                      ]}
                      lineHeight={[
                        company.fantasyName.length > 30 ? "40px" : "44px",
                        company.fantasyName.length > 30 ? "54px" : "42px",
                      ]}
                      textOverflow="ellipsis"
                      fontWeight={700}
                    >
                      {company?.fantasyName}
                    </Heading>
                    <Text fontSize="14px" lineHeight="28px" letterSpacing="-0.0047em">
                      {formattedCertificationDate}.
                    </Text>
                  </Stack>
                </Box>
              </Flex>
            </GridItem>
            <GridItem
              colSpan={[2, 2]}
              display="flex"
              justifyContent={["start", "flex-end"]}
              alignItems="center"
              gap="10px"
              h={["120px", "auto"]}
              pr={["10px", "unset"]}
              pos="relative"
            >
              <Box pos="absolute" left={["-100px", "unset"]} right={["unset", "210px"]}>
                <Bar score={company.score} />
              </Box>
              <Text
                ml={["125px", "20px"]}
                w={["unset", "200px"]}
                as="h5"
                fontSize={["18px", "21px"]}
                fontWeight={["700", "600"]}
                lineHeight="29px"
                letterSpacing={["-0.47px", "-0.0047em"]}
              >
                dos funcionários dizem que Levando-se tudo em conta, este é um excelente lugar para trabalhar
              </Text>
            </GridItem>
          </Grid>
        </Stack>

        <Stack
          p={["30px 0 80px", "60px 0 120px"]}
          spacing={["40px", "80px"]}
          color="#11131C"
        >
          <Heading
            fontSize={["28px", "36px"]}
            lineHeight={["36px", "43px"]}
            letterSpacing={["-0.65px", "-0.007em"]}
            textAlign="center"
            fontWeight={["600", "medium"]}
          >
            Visão Geral da Empresa
          </Heading>
          <Grid templateColumns={["repeat(1, 1fr)", "repeat(3, 1fr)"]} gap={4}>
            <GridItem colSpan={2}>
              <Text
                fontSize="12px"
                lineHeight="20px"
                fontWeight={700}
                letterSpacing="0.0126em"
              >
                SOBRE A EMPRESA
              </Text>
              <Text fontSize="20px" lineHeight="32px" letterSpacing="-0.0047em" mt="14px">
                {company?.description}
              </Text>

              <Grid
                templateColumns={["repeat(2, 1fr)", "repeat(3, 1fr)"]}
                gap={["40px", 4]}
                mt="28px"
              >
                <Stack spacing={["8px", "14px"]}>
                  <Heading
                    as="h3"
                    fontSize={["12px", "14px"]}
                    lineHeight="20px"
                    letterSpacing="0.0126em"
                  >
                    TAMANHO DA EMPRESA
                  </Heading>

                  <Text
                    fontSize={["16px", "20px"]}
                    lineHeight={["28px", "32px"]}
                    letterSpacing={["-0.2px", "unset"]}
                  >
                    {company?.TotalEmployees} ({companySize})
                  </Text>
                </Stack>

                <Stack spacing="14px" as={GridItem} gridColumnStart={[1, 2]}>
                  <Heading
                    as="h3"
                    fontSize={["12px", "14px"]}
                    lineHeight="20px"
                    letterSpacing="0.0126em"
                  >
                    SETOR
                  </Heading>

                  {cleanedSector && (
                    <Text
                      fontSize={["16px", "20px"]}
                      lineHeight={["28px", "32px"]}
                      letterSpacing={["-0.2px", "unset"]}
                    >
                      {cleanedSector}
                    </Text>
                  )}
                </Stack>

                <Stack spacing={["8px", "14px"]}>
                  <Heading
                    as="h3"
                    fontSize={["12px", "14px"]}
                    lineHeight="20px"
                    letterSpacing="0.0126em"
                  >
                    REDES E SITE
                  </Heading>

                  <Flex
                    height={["28px", "28px"]}
                    maxH={["28px", "28px"]}
                    align="center"
                    gap={["10px", "12px"]}
                  >
                    {isValidUrl(company?.website) && (
                      <SocialButton href={company.website} label="Website">
                        <Image src={iconGlobe} alt="Website" height={["28px", "27px"]} />
                      </SocialButton>
                    )}

                    {isValidUrl(company.facebook) && (
                      <SocialButton href={company.facebook} label="Facebook">
                        <Image src={iconFacebook} alt="Facebook" />
                      </SocialButton>
                    )}

                    {isValidUrl(company.instagram) && (
                      <SocialButton href={company.instagram} label="Instagram">
                        <BsInstagram fontSize={"26px"} />
                      </SocialButton>
                    )}

                    {isValidUrl(company.linkedin) && (
                      <SocialButton href={company.linkedin} label="Linkedin">
                        <FaLinkedinIn fontSize={"26px"} />
                      </SocialButton>
                    )}
                  </Flex>
                </Stack>
              </Grid>
            </GridItem>
            <GridItem colSpan={1} display={["none", "flex"]}>
              <Box ml="auto">
                <Text
                  fontSize="12px"
                  lineHeight="20px"
                  letterSpacing="0.0126em"
                  fontWeight={"700"}
                >
                  LOCALIZAÇÃO DA SEDE
                </Text>
                {flagImage && <Image src={flagImage} alt="Company location" mt="14px" />}

                <Text
                  fontSize={"20px"}
                  lineHeight="32px"
                  letterSpacing="-0.0047em"
                  mt="10px"
                >
                  {company?.cidade}, {company?.UF}, Brasil
                </Text>
              </Box>
            </GridItem>
          </Grid>
        </Stack>

        <Divider orientation="horizontal" borderWidth="2px" borderTop="1px solid #eee" />

        {/* <Stack
          p={["30px 0 20px", "60px 0 120px"]}
          spacing="53px"
          color="#11131C"
        >
          <Heading
            fontSize={["28px", "36px"]}
            lineHeight={["36px", "43px"]}
            letterSpacing={["-0.65px", "-0.007em"]}
            textAlign="center"
            fontWeight={["600", "medium"]}
          >
            Dados demográficos dos funcionários
          </Heading>

          <Box height="201px" display={["none", "initial"]}>
            <HorizontalChart />
          </Box>

          <Box height="auto" display={["initial", "none"]}>
            <Text
              fontSize="20px"
              lineHeight="28px"
              fontWeight={600}
              letterSpacing="-0.47px"
              color="#11131C"
              textAlign="center"
              mb="15px"
            >
              Tempo
            </Text>
            <Flex
              gap="14px"
              justify="center"
              color="#11131C"
              width="90%"
              height="62px"
              flexWrap="wrap"
            >
              {chartValues.map((values, index) => (
                <Flex height="22px" align="center" gap="8px" key={index}>
                  <Box height="12px" width="12px" bgColor="#FF1628" />
                  <Text whiteSpace="nowrap" fontWeight={600}>
                    {values.name}
                  </Text>
                </Flex>
              ))}
            </Flex>
            <HorizontalChartMobile />
          </Box>
        </Stack> */}

        <Divider orientation="horizontal" borderWidth="2px" borderTop="1px solid #eee" />

        <Box p={["30px 0 80px", "60px 0 120px"]} color="#11131C">
          <Stack
            spacing={["20px", "33px"]}
            maxW={["unset", "3xl"]}
            mx="auto"
            alignItems="center"
            justify="center"
            textAlign="center"
          >
            <Heading
              as="h2"
              fontSize={["28px", "36px"]}
              lineHeight={["36px", "43px"]}
              letterSpacing={["-0.65px", "-0.007em"]}
              textAlign="center"
              fontWeight={["600", "medium"]}
            >
              Employee Experience
            </Heading>

            <Text
              fontSize="20px"
              lineHeight={["32px", "28px"]}
              letterSpacing="-0.0047em"
              color="black"
              fontWeight={["500", "normal"]}
            >
              O feedback dos colaboradores descrito abaixo foi coletado com a pesquisa
              Great Place To Work ® Trust Index
            </Text>

            <Link
              target="_blank"
              to="https://conteudo.gptw.com.br/checklist-10-passos-como-se-tornar-gptw-2025"
            >
              <Button w="156px" h="51px" variant="primary">
                SAIBA MAIS
              </Button>
            </Link>
          </Stack>

          <Grid
            templateColumns={["repeat(1, 1fr)", "repeat(4, 1fr)"]}
            gap={[4]}
            mt="66px"
            color="#11131C"
          >
            <GridItem colSpan={1}>
              <Text
                w={["unset", "277px"]}
                fontSize="20px"
                lineHeight="32px"
                letterSpacing={["-0.47px", "-0.0047em"]}
                textAlign={["center", "left"]}
              >
                <b> {company.score}% </b>dos colaboradores da{" "}
                <b>{company.fantasyName} </b> dizem que a empresa é um excelente lugar para
                trabalhar comparado com a nota média de 66% das empresas não certificadas
                de 2024
              </Text>
            </GridItem>
            <GridItem
              colSpan={3}
              height={["320px", "264px"]}
              maxH={["320px", "264px"]}
              pos="relative"
              p={0}
            >
              <Box
                pos="absolute"
                width={["90%", "100%"]}
                top={["0", "-40px"]}
                left={["-24px", "0"]}
              >
                <ChartExperience />
              </Box>
              <Stack
                height={["220px", "219px"]}
                w={["18%", "110px"]}
                pos="absolute"
                justifyContent="space-between"
                right={0}
                top={["40px", 0]}
                gap={["8px", "13px"]}
                py="20px"
              >
                <Box>
                  <Text
                    fontSize={["30px", "42px"]}
                    lineHeight="60px"
                    letterSpacing="-0.0112em"
                    color="#11131C"
                    fontWeight={600}
                    textAlign={["right", "unset"]}
                  >
                    {company.score}%
                  </Text>
                </Box>
                <Box>
                  {" "}
                  <Text
                    fontSize={["30px", "42px"]}
                    lineHeight="60px"
                    letterSpacing="-0.0112em"
                    color="#11131C"
                    fontWeight={600}
                    textAlign={["right", "unset"]}
                  >
                    66%
                  </Text>
                </Box>
              </Stack>
            </GridItem>
          </Grid>
          <Box mt="40px">
            <Text
              fontSize="12px"
              lineHeight="20px"
              fontWeight={400}
              letterSpacing="0.0126em"
            >
              ** A nota reflete a Declaração Gestalt final dos colaboradores: <b>"Levando-se tudo em conta, eu diria que este é um excelente lugar para trabalhar."</b>
              <br />
              ** A média GPTW do Trust Index® segue sendo usada para certificação e rankings. Confirma mais detalhes <a href="https://gptw-brasil.zendesk.com/hc/pt-br/articles/41139989816724" target="_blank" rel="noopener noreferrer" style={{ color: "#3182ce", textDecoration: "underline" }}>aqui</a>
            </Text>
          </Box>
        </Box>

        <Divider orientation="horizontal" borderWidth="2px" borderTop="1px solid #eee" />
        {companyImages && (
          <Box p={["30px 0 10px", "66px 0 10px"]} textAlign="center" color="#11131C">
            <Heading
              fontSize={["28px", "36px"]}
              lineHeight={["36px", "43px"]}
              letterSpacing={["-0.65px", "-0.007em"]}
              textAlign="center"
              fontWeight={["600", "medium"]}
              mb={["40px", "32px"]}
            >
              A vida na {company.fantasyName}
            </Heading>
            <Box pos="relative">
              <Button
                height="28px"
                w="48px"
                pos="absolute"
                left="10px"
                display={["none", "flex"]}
                top="320px"
                bg="transparent"
                zIndex={90}
                onClick={() => sliderCarrousel?.slickPrev()}
                _hover={{
                  bgColor: "transparent",
                }}
                _active={{
                  bgColor: "transparent",
                }}
              >
                <Image src={arrowSliderLeft} alt="Arrow left" />
              </Button>

              <Button
                height="28px"
                w="48px"
                display={["none", "flex"]}
                pos="absolute"
                right="10px"
                top="320px"
                bg="transparent"
                zIndex={90}
                onClick={() => sliderCarrousel?.slickNext()}
                _hover={{
                  bgColor: "transparent",
                }}
                _active={{
                  bgColor: "transparent",
                }}
              >
                <Image src={arrowSliderRight} alt="Arrow right" />
              </Button>

              <Slider {...settings} ref={sliderCarrousel => setSlider(sliderCarrousel)}>
                {companyImages?.map((image, index) => (
                  <Flex
                    key={index}
                    w="100%"
                    flexDir="column"
                    alignItems="center"
                    justifyContent="center"
                    height={["360px", "706px"]}
                    px={["0", "60px"]}
                  >
                    <Image
                      src={image}
                      alt={`${company?.fantasyName} photo ${index + 1}`}
                      mx="auto"
                      width={["100%"]}
                      objectFit="contain"
                      objectPosition={"top"}
                    />
                    <Text fontSize="12px" lineHeight="20px" fontWeight={700} mt="14px">
                      {company.fantasyName}, {company.cidade}
                    </Text>
                  </Flex>
                ))}
              </Slider>
            </Box>
          </Box>
        )}
        <CTAWithBackgroundImage />
      </Container>
    </>
  );
}
